<template>
  <section
    ref="sectionRef"
    v-if="content"
    :class="[
      'content-section',
      content.layoutWidth && `content-section--${content.layoutWidth}`,
      customClassName && customClassName,
      `content-section--index-${index}`,
      {
        'content-section--first': isFirst,
        'content-section--last': isLast,
      },
      content.animation && `content-section--animate content-section--animate-${content.animation}`,
      sectionClass,
      marginClasses,
      paddingClasses,
    ]"
    :id="content.anchor"
    :style="sectionStyle"
  >
    <div
      class="content-section__container"
      :class="[content.layoutWidth && `container container--${content.layoutWidth}`, layoutClasses]"
    >
      <PageComponentsDynamicComponent
        v-for="(component, componentIndex) in content.content"
        :key="componentIndex"
        :component="component"
        :index="componentIndex"
      />
    </div>
  </section>
</template>

<script setup lang="ts" async>
  import type { ContentSection } from '~/types/graphql';
  import { generateSpacingClasses, generateCustomSpacingCSS } from '~/utils/spacing';
  import { generateLayoutClasses, generateCustomLayoutCSS } from '~/utils/layout';
  import { sanitizeCSS } from '~/utils/css';
  import { useElementVisibility } from '@vueuse/core';

  const { content, index, isFirst, isLast } = defineProps<{
    content: ContentSection;
    index: number;
    isFirst: boolean;
    isLast: boolean;
  }>();

  const customClassName = content.customClassName;

  const sectionStyle = computed(() => {
    const style: Record<string, string> = {};
    if (content.backgroundColor) {
      style.backgroundColor = `${content.backgroundColor}`;
    }
    return style;
    
  });

  // Generate unique ID for this section
  const sectionId = useId();

  // Generate unique class name for this section
  const sectionClass = `content-section--${sectionId}`;

  // Collect all CSS to inject
  const cssToInject: string[] = [];

  // Handle custom CSS injection for margins and paddings
  if (content.margins?.customSpacing) {
    const customCSS = generateCustomSpacingCSS(content.margins.customSpacing, 'margin', sectionClass);
    if (customCSS) {
      cssToInject.push(customCSS);
    }
  }

  if (content.paddings?.customSpacing) {
    const customCSS = generateCustomSpacingCSS(content.paddings.customSpacing, 'padding', sectionClass);
    if (customCSS) {
      cssToInject.push(customCSS);
    }
  }

  // Handle custom CSS injection for layout
  if (content.layout) {
    const customLayoutCSS = generateCustomLayoutCSS(content.layout, sectionClass);
    if (customLayoutCSS) {
      cssToInject.push(customLayoutCSS);
    }
  }

  // Add sanitized CSS if inlineStyles exist
  if (content.inlineStyles) {
    const sanitizedCSS = await sanitizeCSS(content.inlineStyles, sectionClass);
    if (sanitizedCSS) {
      cssToInject.push(sanitizedCSS);
    }
  }

  // Inject all CSS in a single useHead call
  if (cssToInject.length > 0) {
    // Minify all CSS together on server side
    let finalCSS = cssToInject.join('\n');

    if (import.meta.server) {
      // only sever side, keeps clean-css from being bundled in the client
      try {
        const CleanCSS = await import('clean-css');
        const cleanCSS = new CleanCSS.default({
          compatibility: 'ie9',
          level: {
            1: {
              all: true,
              normalizeUrls: false,
              removeEmpty: true,
              removeWhitespace: true,
              specialComments: 'none',
            },
            2: {
              all: true,
            },
          },
        });

        const result = cleanCSS.minify(finalCSS);
        finalCSS = result.styles || finalCSS;
      } catch (error) {
        console.error('Error minifying combined CSS:', error);
      }
    }

    useHead({
      style: [
        {
          innerHTML: finalCSS,
          key: `layout-css-${sectionId}`,
        },
      ],
    });
  }

  // Generate margin and padding classes
  const marginClasses = computed(() => generateSpacingClasses('margin', content.margins, sectionClass));
  const paddingClasses = computed(() => generateSpacingClasses('padding', content.paddings, sectionClass));

  // Generate layout classes
  const layoutClasses = computed(() => {
    const generatedClasses = generateLayoutClasses(content.layout, sectionClass);
    if (content.layoutWidth === 'page') {
      generatedClasses.push('container container--page-content');
    } else if (content.layoutWidth === 'content') {
      generatedClasses.push('container container--post-content');
    } else {
      // for screen we don't need to add any classes
    }

    return generatedClasses;
  });

  const sectionRef = useTemplateRef<HTMLElement | null>('sectionRef');
  const isVisible = useElementVisibility(sectionRef, {
    rootMargin: '0px 0px -250px 0px'
  });

  // Handle animation logic on mount
  onMounted(() => {
    if (!sectionRef.value) return;
    
    // If element is already visible, don't add inactive class
    if (isVisible.value) {
      return;
    }
    
    // Add inactive class for animation if element is not visible
    if (content.animation) {
      sectionRef.value.classList.add('content-section--animate--inactive');
    }
  });

  // Watch for visibility changes and trigger animation
  watch(isVisible, (visible) => {
    if (visible && sectionRef.value) {
      sectionRef.value.classList.remove('content-section--animate--inactive');
    }
  });
</script>

<style lang="scss">
  .content-section--animate {
    @apply relative z-0;
    @apply opacity-100 transform-gpu translate-y-0 blur-0 scale-100  rotate-0 skew-x-0 skew-y-0 translate-x-0;
    transition: all 1s cubic-bezier(0.00,0.00,0.58,1.00);
    

    &--inactive {
      &.content-section {
        &--animate-fade {
          @apply opacity-0 transform-gpu translate-y-0 blur-lg scale-100  rotate-0 skew-x-0 skew-y-0 translate-x-0;
        }

        &--animate-slide_up {
         transition-duration: 0.6s;
          @apply opacity-100 transform-gpu translate-y-[100px] blur-0 scale-100  rotate-0 skew-x-0 skew-y-0 translate-x-0;
        }

        &--animate-slide_down {
          @apply opacity-0 transform-gpu translate-y-[-100px] blur-lg scale-95  rotate-0 skew-x-0 skew-y-0 translate-x-0;
        }

        &--animate-slide_left {
          @apply opacity-0 transform-gpu translate-x-[-100px] blur-lg scale-95  rotate-0 skew-x-0 skew-y-0 translate-y-0;
        }

        &--animate-slide_right {
          @apply opacity-0 transform-gpu translate-x-[100px] blur-lg scale-95  rotate-0 skew-x-0 skew-y-0 translate-y-0;
        }
      }
    }
  }
</style>
